import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { CountChips } from '../../../../components/ui/CountChips';
import { DataTable } from '../../../../components/ui/DataTable';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ErrorState } from '../../../../components/ui/ErrorState';
import {
  setStoreSalesFilters,
  stockSyncChannelSelected,
  stockSyncLeft,
  stockSyncWatched,
} from '../../../../features/stockSync/stockSyncSlice';
import { fetchStockSync, fetchStockSyncChannels, fetchStoreSales } from '../../../../features/stockSync/stockSyncThunks';
import { selectChannelOptions, selectUsableChannels } from '../../../../features/stockSync/stockSyncSelectors';
import { STOCK_SYNC_WATCHER, STORE_SALE_FILTER } from '../../../../features/stockSync/stockSyncConstants';
import {
  formatSyncDate,
  pickDefaultChannelId,
  shopDomain,
  storeSaleFilterOptions,
  storeSaleReasonText,
  storeSaleShortText,
  storeSaleTakenText,
  storeSaleTone,
  storeSalesSummary,
} from '../../../../features/stockSync/stockSyncUtils';
import { selectCurrentWarehouse } from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const INVENTORY_PATH = '/warehouse/inventory';

/**
 * `/warehouse/inventory/shopify/sales?store=<id>`: the sales of a Shopify store
 * whose order never reached Ninja, because the store ships them itself, and
 * what Ninja took out of its free stock for each. Only orders made since the
 * publication was confirmed count, each after a few minutes waiting for its
 * order; what Ninja gives back goes to the locations it came from. The store
 * comes in the address; without it, the page picks it as the dialogs do.
 */
export const InventoryShopifySales = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeParam = Number(searchParams.get('store')) || null;

  const warehouse = useSelector(selectCurrentWarehouse);
  const channels = useSelector(selectUsableChannels);
  const channelOptions = useSelector(selectChannelOptions);
  const { channelsLoaded, selectedId, detail, detailError, storeSales } = useSelector((state) => state.stockSync);
  const { rows, totalCount, loading, error, filters } = storeSales;

  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    dispatch(stockSyncWatched(STOCK_SYNC_WATCHER.STORE_SALES));
    return () => dispatch(stockSyncLeft(STOCK_SYNC_WATCHER.STORE_SALES));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchStockSyncChannels({ warehouseId: warehouse?.id ?? null }));
  }, [dispatch, warehouse?.id]);

  // The store in the address is the one in use; without one, the page picks it.
  useEffect(() => {
    if (storeParam) {
      dispatch(stockSyncChannelSelected(storeParam));
      return;
    }
    if (!channelsLoaded) return;
    const settingId = pickDefaultChannelId(channels, { selectedId });
    if (settingId) setSearchParams({ store: String(settingId) }, { replace: true });
  }, [dispatch, storeParam, channelsLoaded, channels, selectedId, setSearchParams]);

  useEffect(() => {
    if (selectedId) dispatch(fetchStockSync({ settingId: selectedId }));
  }, [dispatch, selectedId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setStoreSalesFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  useEffect(() => {
    if (selectedId) dispatch(fetchStoreSales());
  }, [dispatch, selectedId, filters.search, filters.status, filters.page, filters.rowsPerPage]);

  const columns = [
    {
      header: 'ORDEN',
      cell: (row) => (
        <span
          className="font-bold font-mono text-ninja-dark dark:text-white"
          data-testid="shopify-sale-row"
          data-sale-id={row.id}
          data-status={row.status}
        >
          {row.order_name}
        </span>
      ),
    },
    {
      header: 'PRODUCTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[260px]">
          <span className="font-mono font-semibold text-ninja-dark dark:text-white">{row.sku}</span>
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.title}>
            {row.title}
          </span>
        </div>
      ),
    },
    {
      header: 'VENDIDAS',
      cell: (row) => <span className="tabular-nums">{formatQuantity(row.quantity)}</span>,
    },
    {
      // What Ninja took of what the store sold: what was not free stays as the shortfall.
      header: 'DESCONTADAS',
      cell: (row) => {
        const short = storeSaleShortText(row);
        return (
          <div className="flex flex-col tabular-nums">
            <span>{storeSaleTakenText(row)}</span>
            {short && <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">{short}</span>}
          </div>
        );
      },
    },
    {
      header: 'ESTADO',
      cell: (row) => {
        const reason = storeSaleReasonText(row);
        return (
          <div className="flex flex-col items-start gap-1 max-w-[280px]">
            <ToneBadge tone={storeSaleTone(row)} testId="shopify-sale-status">
              {row.status_label}
            </ToneBadge>
            {reason && <span className="text-xs text-text-muted whitespace-normal">{reason}</span>}
          </div>
        );
      },
    },
    {
      header: 'FECHA',
      cell: (row) => (
        <div className="flex flex-col">
          <span>{formatSyncDate(row.created_at)}</span>
          {row.returned_at && (
            <span className="text-xs text-text-muted">Devuelta el {formatSyncDate(row.returned_at)}</span>
          )}
        </div>
      ),
    },
  ];

  const isFiltering = filters.search !== '' || filters.status !== STORE_SALE_FILTER.ALL;
  const emptyMessage = isFiltering
    ? 'No hay ventas que coincidan con estos filtros.'
    : 'Todavía no hay ventas de la tienda que Ninja haya descontado. Solo cuentan las órdenes creadas desde que se confirmó la publicación, y cada venta espera unos 5 minutos a que llegue su orden.';

  return (
    <div data-testid="shopify-sales-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Inventario" to={INVENTORY_PATH} current="Ventas de la tienda" />

      <DetailHero
        testId="shopify-sales-hero"
        media={<ShoppingBag size={36} strokeWidth={1.5} />}
        title="Ventas de la tienda"
        meta={
          detail && (
            <>
              <span>{detail.channel.company.name}</span>
              <span>{shopDomain(detail.channel.shop_url)}</span>
              <span>
                {detail.channel.warehouse.name}
                {detail.location ? ` → «${detail.location.name}»` : ' · sin ubicación en la tienda'}
              </span>
              <span data-testid="shopify-sales-summary">{storeSalesSummary(detail.store_sales)}</span>
            </>
          )
        }
      />

      <p className="text-sm text-text-secondary max-w-3xl" data-testid="shopify-sales-rule">
        Una venta de la tienda cuya orden no llega a Ninja, porque la despacha la tienda, se descuenta del stock libre
        de Ninja. Si la tienda la cancela, o la orden llega a Ninja, lo descontado vuelve a las mismas ubicaciones.
      </p>

      {detailError && (
        <ErrorState
          variant="inline"
          title="No se pudo leer la tienda"
          description={detailError}
          onRetry={() => dispatch(fetchStockSync({ settingId: selectedId }))}
        />
      )}

      {!selectedId && channelsLoaded && channels.length === 0 && (
        <p className="text-sm text-text-secondary" data-testid="shopify-sales-none">
          Ningún cliente con el WMS en esta bodega tiene su tienda Shopify conectada.
        </p>
      )}

      {detail && (
        <CountChips
          options={storeSaleFilterOptions(detail.store_sales)}
          value={filters.status}
          onChange={(status) => dispatch(setStoreSalesFilters({ status }))}
          allValue={STORE_SALE_FILTER.ALL}
          allLabel="Todas"
          testId="shopify-sales-status"
        />
      )}

      <FilterBar variant="bare" testId="shopify-sales-filters">
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por orden o SKU..."
          testId="shopify-sales-search"
        />
        {channelOptions.length > 1 && (
          <FilterableSelect
            label="Tienda:"
            value={selectedId ?? ''}
            onChange={(settingId) => setSearchParams({ store: String(settingId) })}
            options={channelOptions}
            placeholder="Elige la tienda"
            searchPlaceholder="Buscar cliente o tienda..."
            panelMinWidth={280}
            testId="shopify-sales-store"
          />
        )}
      </FilterBar>

      {selectedId && (
        <DataTable
          columns={columns}
          data={rows}
          totalItems={totalCount}
          currentPage={filters.page}
          itemsPerPage={filters.rowsPerPage}
          isLoading={loading}
          error={error}
          onRetry={() => dispatch(fetchStoreSales())}
          itemLabel="ventas"
          emptyMessage={emptyMessage}
          onPageChange={(page) => dispatch(setStoreSalesFilters({ page }))}
          onRowsPerPageChange={(rowsPerPage) => dispatch(setStoreSalesFilters({ rowsPerPage }))}
        />
      )}

      {!selectedId && channels.length > 1 && (
        <p className="text-sm text-text-secondary" data-testid="shopify-sales-choose">
          Elige arriba la tienda para ver sus ventas.
        </p>
      )}
    </div>
  );
};
